// src/components/notify/console/NotifyDispatchDrawer.js
// One dispatch: delivery funnel (delivered → read → acknowledged), per-person status, channel
// results, failures, and actions (remind unread / unacknowledged, cancel, reschedule).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerFooter, DrawerHeader, DrawerOverlay, Flex, HStack, Icon, Input, Progress, SimpleGrid, Skeleton, Stack, Table,
    Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue, useToast, VStack,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdCancel, MdCheckCircle, MdDoneAll, MdNotificationsActive, MdSchedule, MdTrendingUp } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import { roleMeta } from '../../../config/roles';
import { categoryMeta, fmtDateTime, fmtDuration, priorityMeta, sourceMeta, statusMeta, timeAgo } from '../notifyFormat';

function Funnel({ label, value, total, color }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const p = total ? Math.round((value / total) * 100) : 0;
    return (
        <Box>
            <Flex justify="space-between" fontSize="xs" mb={1}><Text fontWeight="700">{label}</Text><Text color={muted}>{value}/{total} · {p}%</Text></Flex>
            <Progress value={p} colorScheme={color} size="sm" borderRadius="full" aria-label={`${label} ${p}%`} />
        </Box>
    );
}

const toLocalInput = (d) => {
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NotifyDispatchDrawer({ dispatchId, onClose, onChanged }) {
    const toast = useToast();
    const muted = useColorModeValue('gray.500', 'gray.400');
    const card = useColorModeValue('gray.50', 'whiteAlpha.50');
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const [busy, setBusy] = useState(null);
    const [when, setWhen] = useState('');
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const load = useCallback(async (signal) => {
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const data = await api.getDispatch(dispatchId, signal);
            if (!isMounted.current) return;
            setState({ loading: false, data, error: null });
            if (data?.scheduled_for) setWhen(toLocalInput(new Date(data.scheduled_for)));
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setState({ loading: false, data: null, error: e });
        }
    }, [dispatchId]);

    useEffect(() => {
        if (!dispatchId) return undefined;
        const ctrl = new AbortController();
        load(ctrl.signal);
        return () => ctrl.abort();
    }, [dispatchId, load]);

    const act = useCallback(async (kind, fn, ok) => {
        setBusy(kind);
        try {
            await fn();
            toast({ status: 'success', title: ok, duration: 2500 });
            onChanged?.();
            await load();
        } catch (e) {
            toast({ status: 'error', title: 'Action failed', description: e.message });
        } finally {
            if (isMounted.current) setBusy(null);
        }
    }, [toast, onChanged, load]);

    const d = state.data;
    const del = d?.delivery;
    const st = statusMeta(d?.status);
    const src = sourceMeta(d?.source);
    const cat = categoryMeta(d?.category);
    const pr = priorityMeta(d?.priority);

    return (
        <Drawer isOpen={Boolean(dispatchId)} onClose={onClose} size="lg" placement="right">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" pr={10}>
                    {d ? (
                        <VStack align="stretch" spacing={1}>
                            <Text noOfLines={2}>{d.title}</Text>
                            <HStack spacing={1.5} flexWrap="wrap">
                                <Badge colorScheme={st.color}>{st.label}</Badge>
                                <Badge colorScheme={pr.color} variant="subtle">{pr.label}</Badge>
                                <Badge colorScheme={cat.color} variant="subtle">{cat.label}</Badge>
                                <Badge colorScheme={src.ai ? 'purple' : 'gray'} variant="outline">{src.ai && <Icon as={MdAutoAwesome} mr={0.5} />}{src.label}</Badge>
                                {d.requires_ack && <Badge colorScheme="red" variant="outline">Ack required</Badge>}
                            </HStack>
                        </VStack>
                    ) : 'Notification'}
                </DrawerHeader>
                <DrawerBody>
                    {state.error ? (
                        <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription flex="1">{state.error.message}</AlertDescription><Button size="sm" onClick={() => load()}>Retry</Button></Alert>
                    ) : state.loading && !d ? (
                        <Stack spacing={3}>{[0, 1, 2, 3].map(i => <Skeleton key={i} h="60px" borderRadius="lg" />)}</Stack>
                    ) : d && (
                        <VStack align="stretch" spacing={5}>
                            <Box bg={card} borderRadius="lg" p={3}>
                                <Text fontSize="sm" whiteSpace="pre-line">{d.body}</Text>
                                <Text fontSize="xs" color={muted} mt={2}>
                                    To {d.audience_label || '—'} · {d.status === 'scheduled' ? `scheduled for ${fmtDateTime(d.scheduled_for)}` : `sent ${fmtDateTime(d.completed_at || d.created_at)}`}
                                    {d.created_by_name ? ` · by ${d.created_by_name}` : ''}{d.parent_dispatch_id ? ' · reminder' : ''}
                                </Text>
                                {d.last_error && <Text fontSize="xs" color="orange.400" mt={1}>{d.last_error}</Text>}
                            </Box>

                            {d.status !== 'scheduled' && d.status !== 'cancelled' && del && (
                                <Stack spacing={3}>
                                    <Funnel label="Delivered" value={del.delivered} total={del.recipients} color="green" />
                                    <Funnel label="Read" value={del.read} total={del.recipients} color="teal" />
                                    {del.ack_required > 0 && <Funnel label="Acknowledged" value={del.acknowledged} total={del.ack_required} color={del.acknowledged < del.ack_required ? 'red' : 'green'} />}
                                    <SimpleGrid columns={3} spacing={2} fontSize="xs">
                                        <Box><Text color={muted}>Avg time to read</Text><Text fontWeight="700">{fmtDuration(del.avg_read_sec)}</Text></Box>
                                        <Box><Text color={muted}>Avg time to ack</Text><Text fontWeight="700">{fmtDuration(del.avg_ack_sec)}</Text></Box>
                                        <Box><Text color={muted}>Escalated</Text><Text fontWeight="700" color={del.escalated ? 'red.400' : undefined}>{del.escalated}</Text></Box>
                                    </SimpleGrid>
                                    <HStack spacing={2} flexWrap="wrap" fontSize="xs">
                                        <Badge>live {d.realtime_count}</Badge>
                                        <Badge>browser push {d.webpush_count}</Badge>
                                        <Badge>mobile push {d.fcm_count ?? 0}</Badge>{/* --- UPDATE (FCM channel) */}
                                        <Badge>email {d.email_count}</Badge>
                                        {d.suppressed_count > 0 && <Tooltip label="Quiet hours, muted, or push off for this topic — still delivered to the inbox" hasArrow><Badge colorScheme="orange">silent {d.suppressed_count}</Badge></Tooltip>}
                                        {del.channels.filter(c => c.status !== 'sent').map(c => <Badge key={`${c.channel}-${c.status}`} colorScheme="red">{c.channel} {c.status} {c.count}</Badge>)}
                                    </HStack>
                                </Stack>
                            )}

                            {del?.people?.length > 0 && (
                                <Box>
                                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" mb={1}>Recipients ({del.recipients})</Text>
                                    <Box overflowX="auto" maxH="320px" overflowY="auto">
                                        <Table size="sm">
                                            <Thead><Tr><Th>Person</Th><Th>Role</Th><Th>Status</Th></Tr></Thead>
                                            <Tbody>
                                                {del.people.map(p => {
                                                    const rm = roleMeta(p.role);
                                                    const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username || p.user_id;
                                                    return (
                                                        <Tr key={p.notification_id}>
                                                            <Td fontSize="sm">{name}</Td>
                                                            <Td><Badge colorScheme={rm.color} variant="subtle">{rm.short}</Badge></Td>
                                                            <Td>
                                                                <HStack spacing={1}>
                                                                    {p.acknowledged_at ? <Tooltip label={`Acknowledged ${timeAgo(p.acknowledged_at)}`} hasArrow><span><Icon as={MdCheckCircle} color="green.400" aria-label="Acknowledged" /></span></Tooltip>
                                                                        : p.is_read ? <Tooltip label={`Read ${timeAgo(p.read_at)}`} hasArrow><span><Icon as={MdDoneAll} color="teal.400" aria-label="Read" /></span></Tooltip>
                                                                            : p.delivered_at ? <Tooltip label={`Delivered ${timeAgo(p.delivered_at)}`} hasArrow><span><Icon as={MdNotificationsActive} color="blue.400" aria-label="Delivered" /></span></Tooltip>
                                                                                : <Text fontSize="xs" color={muted}>In inbox</Text>}
                                                                    {p.escalation_level > 0 && <Badge colorScheme="red" variant="outline" fontSize="2xs"><Icon as={MdTrendingUp} /> L{p.escalation_level}</Badge>}
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                </Box>
                            )}

                            {del?.failures?.length > 0 && (
                                <Box>
                                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" mb={1}>Failures</Text>
                                    {del.failures.map((f, i) => <Text key={i} fontSize="xs">{f.channel} · user {f.subject_id}: {f.error}</Text>)}
                                </Box>
                            )}

                            {d.status === 'scheduled' && (
                                <Box bg={card} borderRadius="lg" p={3}>
                                    <Text fontSize="sm" fontWeight="700" mb={2}>Reschedule</Text>
                                    <HStack>
                                        <Input size="sm" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="New send time" />
                                        <Button size="sm" leftIcon={<MdSchedule />} isLoading={busy === 'reschedule'} onClick={() => act('reschedule', () => api.rescheduleDispatch(d.id, new Date(when).toISOString()), 'Rescheduled')}>Save</Button>
                                    </HStack>
                                </Box>
                            )}
                        </VStack>
                    )}
                </DrawerBody>
                {d && (
                    <DrawerFooter borderTopWidth="1px" gap={2} flexWrap="wrap">
                        {['scheduled', 'queued'].includes(d.status) && (
                            <Button size="sm" colorScheme="red" variant="outline" leftIcon={<MdCancel />} isLoading={busy === 'cancel'} onClick={() => act('cancel', () => api.cancelDispatch(d.id), 'Cancelled')}>Cancel send</Button>
                        )}
                        {del && del.read < del.recipients && ['sent', 'partial'].includes(d.status) && (
                            <Button size="sm" variant="outline" isLoading={busy === 'unread'} onClick={() => act('unread', () => api.resendDispatch(d.id, 'unread'), 'Reminder sent to unread recipients')}>Remind unread ({del.recipients - del.read})</Button>
                        )}
                        {del && del.ack_required > del.acknowledged && ['sent', 'partial'].includes(d.status) && (
                            <Button size="sm" colorScheme="red" isLoading={busy === 'ack'} onClick={() => act('ack', () => api.resendDispatch(d.id, 'unacknowledged'), 'Reminder sent')}>Remind unacknowledged ({del.ack_required - del.acknowledged})</Button>
                        )}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
}
